/**
 * The persisted session (`localStorage['reefguide_tokens']`, AGENTS.md §16).
 *
 * The refresh token lives in the same entry as the access and ID tokens — it
 * has been stored there since the login was built, so the silent refresh adds
 * no new place a script could read a token from. (An httpOnly cookie is not an
 * option today: the API runs on another origin than the SPA and CloudFront
 * forwards no cookies.)
 */
export const TOKENS_KEY = 'reefguide_tokens';

export interface StoredTokens {
  accessToken: string;
  idToken: string;
  /** '' when the session has none (e.g. E2E fixture tokens). */
  refreshToken: string;
  /** Epoch ms when the access token expires. */
  expiresAt: number;
}

type Listener = (tokens: StoredTokens | null) => void;
const listeners = new Set<Listener>();

/** Fallback lifetime when a token carries no `exp` claim (Cognito default: 1 h). */
const DEFAULT_LIFETIME_S = 3600;

export function decodeJwtClaims(token: string): Record<string, unknown> {
  try {
    const part = token.split('.')[1];
    if (!part) return {};
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const claims: unknown = JSON.parse(json);
    return claims !== null && typeof claims === 'object' ? (claims as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Expiry of an access token: its `exp` claim, else now + expiresIn (default 1 h). */
export function computeExpiresAt(accessToken: string, expiresIn?: number, now: number = Date.now()): number {
  const exp = decodeJwtClaims(accessToken)['exp'];
  if (typeof exp === 'number' && Number.isFinite(exp)) return exp * 1000;
  const lifetime = typeof expiresIn === 'number' && expiresIn > 0 ? expiresIn : DEFAULT_LIFETIME_S;
  return now + lifetime * 1000;
}

/** The Cognito username (needed by the backend for SECRET_HASH on refresh). */
export function cognitoUsername(tokens: Pick<StoredTokens, 'accessToken' | 'idToken'>): string | undefined {
  const id = decodeJwtClaims(tokens.idToken);
  const access = decodeJwtClaims(tokens.accessToken);
  for (const value of [id['cognito:username'], access['username'], id['sub'], access['sub']]) {
    if (typeof value === 'string' && value !== '') return value;
  }
  return undefined;
}

/** The stored session, or null when there is none / it is malformed. Never throws. */
export function readStoredTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTokens> | null;
    if (!parsed || typeof parsed.accessToken !== 'string' || parsed.accessToken === '') return null;
    return {
      accessToken: parsed.accessToken,
      idToken: typeof parsed.idToken === 'string' ? parsed.idToken : '',
      refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : '',
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0,
    };
  } catch {
    return null;
  }
}

function notify(tokens: StoredTokens | null): void {
  listeners.forEach((listener) => listener(tokens));
}

export function writeStoredTokens(tokens: StoredTokens): void {
  try {
    localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
  } catch {
    // storage unavailable — the session then lasts for this page only
  }
  notify(tokens);
}

export function clearStoredTokens(): void {
  try {
    localStorage.removeItem(TOKENS_KEY);
  } catch {
    // storage unavailable
  }
}

/** Called after a silent refresh stored new tokens (AuthProvider updates its state). */
export function subscribeStoredTokens(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
